import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { InputGroup, InputGroupItem } from '@/components/ui/input-group';
import { Switch } from '@/components/ui/switch';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';
import { ArrowPathIcon, CameraIcon } from '@heroicons/react/24/outline';
import { WorkspaceAvatar } from '@/components/ui/workspace-avatar';
import useSWR from 'swr';
import { useCredits } from '@/components/CreditDisplay';
import { User } from '@/lib/hooks/useUser';

interface CreditBalance {
  balance: number;
}

interface CreditTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string | null;
  createdAt: string;
}

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

function formatTransactionType(type: string): string {
  switch (type) {
    case 'allocation': return 'Allocation';
    case 'debit': return 'Used';
    case 'topup': return 'Top-up';
    case 'refund': return 'Refund';
    case 'admin_reset': return 'Reset';
    case 'admin_adjustment': return 'Adjustment';
    default: return type;
  }
}

function UsageHistoryComponent() {
  const { isLoading: balanceLoading } = useSWR<CreditBalance>('/api/credits', fetcher);
  const { data: txData, isLoading: txLoading } = useSWR<{ transactions: CreditTransaction[] }>(
    '/api/credits/transactions?limit=50',
    fetcher
  );

  const isLoading = balanceLoading || txLoading;

  if (isLoading) {
    return (
      <div className="space-y-4 ml-3">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const transactions = txData?.transactions ?? [];

  return (
    <div className="space-y-4 ml-3 pr-4 pb-1">
      {transactions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No transactions yet.</p>
      ) : (
        <div className="border border-panel-border rounded-xs overflow-hidden">
          <table className="w-full text-xs font-mono tracking-tight">
            <thead>
              <tr className="bg-surface-sunken border-b border-table-grid">
                <th className="text-left px-3 py-2 font-medium text-content-secondary">Date</th>
                <th className="text-left px-3 py-2 font-medium text-content-secondary">Type</th>
                <th className="text-left px-3 py-2 font-medium text-content-secondary">Description</th>
                <th className="text-right px-3 py-2 font-medium text-content-secondary">Amount</th>
                <th className="text-right px-3 py-2 font-medium text-content-secondary">Balance</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className="border-b border-table-grid last:border-b-0">
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {new Date(tx.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatTransactionType(tx.type)}</td>
                  <td className="px-3 py-2 text-muted-foreground truncate max-w-48">{tx.description || '-'}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap font-medium ${tx.amount > 0 ? 'text-success-foreground' : 'text-content-heading'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('en-GB')}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground whitespace-nowrap">
                    {tx.balanceAfter.toLocaleString('en-GB')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface Workspace {
  id: number;
  name: string;
  role: string;
  createdAt: string;
  avatarColor?: string;
  avatarUrl?: string;
}

function WorkspaceCard({ ws, isCurrent, onSwitch, onRenamed, onDeleted }: { ws: Workspace; isCurrent: boolean; onSwitch: () => void; onRenamed: () => void; onDeleted: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [renamingLoading, setRenamingLoading] = useState(false);
  const [nameValue, setNameValue] = useState(ws.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await fetch(`/api/workspaces/${ws.id}/avatar`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      onRenamed(); // refresh list
    } catch {}
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleRename = async () => {
    if (!nameValue.trim() || nameValue.trim() === ws.name) { setRenaming(false); return; }
    setRenamingLoading(true);
    try {
      await fetch(`/api/workspaces/${ws.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameValue.trim() }),
      });
      onRenamed();
      setRenaming(false);
    } catch {}
    setRenamingLoading(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await fetch(`/api/workspaces/${ws.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      onDeleted();
    } catch {}
    setIsDeleting(false);
    setConfirmDelete(false);
  };

  return (
    <>
      <Card className="p-4 rounded-lg border border-panel-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between">
            <div className="relative group cursor-pointer shrink-0 mr-3" onClick={() => avatarInputRef.current?.click()}>
              <WorkspaceAvatar name={ws.name} color={ws.avatarColor} imageUrl={ws.avatarUrl} size="lg" />
              <div className="absolute inset-0 rounded-full bg-scrim-media/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <CameraIcon className="h-5 w-5 text-on-accent" />
              </div>
              <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarUpload} />
            </div>
            <div className="flex-1 min-w-0">
              {renaming ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') { setRenaming(false); setNameValue(ws.name); } }}
                    className="flex-1 h-12 px-4 rounded-full border border-control-border text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <Button variant="primary" onClick={handleRename} disabled={renamingLoading || !nameValue.trim()}>
                    {renamingLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button variant="secondary" onClick={() => { setRenaming(false); setNameValue(ws.name); }}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{ws.name}</p>
                  {isCurrent && (
                    <span className="text-xs bg-success-surface-strong text-success-foreground-strong px-2 py-0.5 rounded-full">Current</span>
                  )}
                </div>
              )}
            </div>
            {!renaming && (
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <Button variant="secondary" size="xs" onClick={() => { setRenaming(true); setNameValue(ws.name); }}>
                  Rename
                </Button>
                {!isCurrent && (
                  <>
                    <Button variant="primary" size="xs" onClick={onSwitch}>
                      Switch
                    </Button>
                    <Button variant="destructive" size="xs" onClick={() => setConfirmDelete(true)}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{ws.name}</strong>? All contacts, campaigns, and data in this workspace will be permanently removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Workspace'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function WorkspacesSection() {
  const { data: team, mutate: mutateTeam } = useSWR<{ id: number; name: string }>('/api/team', fetcher);
  const { data: workspaces, mutate: mutateWorkspaces } = useSWR<Workspace[]>('/api/workspaces', fetcher);
  const [isCreating, setIsCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const allWorkspaces = Array.isArray(workspaces) ? workspaces : [];

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setIsCreating(true);
    try {
      await fetch('/api/workspaces', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: createName.trim() }),
      });
      mutateWorkspaces();
      setCreateName('');
      setShowCreateForm(false);
    } catch {}
    setIsCreating(false);
  };

  const handleSwitch = async (workspaceId: number) => {
    setIsSwitching(true);
    try {
      await fetch('/api/workspaces/switch', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId }),
      });
      // Reload to pick up new team context everywhere
      window.location.href = '/dashboard/campaigns';
    } catch {
      setIsSwitching(false);
    }
  };

  const handleRenamed = () => {
    mutateWorkspaces();
    mutateTeam();
  };

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Workspaces</h3>
      {isSwitching && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">Switching workspace...</p>
        </div>
      )}
      {!isSwitching && (
        <div className="space-y-3">
          {allWorkspaces.map((ws) => (
            <WorkspaceCard
              key={ws.id}
              ws={ws}
              isCurrent={ws.id === team?.id}
              onSwitch={() => handleSwitch(ws.id)}
              onRenamed={handleRenamed}
              onDeleted={() => mutateWorkspaces()}
            />
          ))}

          {showCreateForm ? (
            <Card className="p-4 rounded-lg border border-dashed border-panel-border">
              <CardContent className="p-0">
                <p className="text-sm font-medium mb-3">New Workspace</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="Workspace name"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                    className="flex-1 h-12 px-4 rounded-full border border-control-border text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <Button variant="primary" onClick={handleCreate} disabled={isCreating || !createName.trim()}>
                    {isCreating ? 'Creating...' : 'Create'}
                  </Button>
                  <Button variant="secondary" onClick={() => { setShowCreateForm(false); setCreateName(''); }}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div>
              <Button variant="primary" onClick={() => setShowCreateForm(true)} className="w-full">
                Create new workspace
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                {allWorkspaces.length} workspace{allWorkspaces.length !== 1 ? 's' : ''} used
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UsageSection() {
  const { balance, limit, planName, isLoading } = useCredits();
  const used = Math.max(0, limit - balance);
  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Usage</h3>
      <div className="space-y-6">
        <Card className="p-4 rounded-lg border border-panel-border">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-md font-medium text-foreground">{planName || 'No'} Plan</span>
              <span className="text-sm text-muted-foreground">
                {isLoading ? '...' : `${balance.toLocaleString('en-GB')} / ${limit.toLocaleString('en-GB')} credits remaining`}
              </span>
            </div>
            <div className="w-full h-3 bg-surface-track rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300 bg-meter-fill"
                style={{ width: `${100 - percentage}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {used.toLocaleString('en-GB')} of {limit.toLocaleString('en-GB')} credits used this month. Credits reset each billing cycle.
            </p>
          </div>
        </Card>

        <Card className="p-1 rounded-lg border border-panel-border">
          <CardContent className="p-1 pb-3">
            <h3 className="text-md font-medium mb-6 ml-3 mt-2">Credit Usage</h3>
            <UsageHistoryComponent />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface AccountSettingsDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  initialSection?: string;
}

export function AccountSettingsDialog({ isOpen, setIsOpen, initialSection }: AccountSettingsDialogProps) {
  const [activeSection, setActiveSection] = useState(initialSection || 'general');
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (isOpen && initialSection) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);

  const sections = [
    { id: 'general', label: 'General' },
    { id: 'workspaces', label: 'Workspaces' },
    { id: 'usage', label: 'Usage' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'security', label: 'Security' },
  ];

  function AccountFormWithData() {
    const { data: user, isLoading } = useSWR<User>('/api/user', fetcher);
    
    if (isLoading) {
      return (
        <InputGroup className="max-w-md">
          <InputGroupItem
            id="name"
            name="name"
            placeholder="Loading..."
            inlineLabel="Name"
            labelWidth="120px"
            disabled
          />
          <InputGroupItem
            id="email"
            name="email"
            type="email"
            placeholder="Loading..."
            inlineLabel="Email"
            labelWidth="120px"
            disabled
          />
        </InputGroup>
      );
    }
    
    return (
      <InputGroup className="max-w-md">
        <InputGroupItem
          id="name"
          name="name"
          placeholder="Enter your name"
          inlineLabel="Name"
          labelWidth="120px"
          defaultValue={user?.name ?? ''}
          required
        />
        <InputGroupItem
          id="email"
          name="email"
          type="email"
          placeholder="Enter your email"
          inlineLabel="Email"
          labelWidth="120px"
          defaultValue={user?.email ?? ''}
          required
        />
      </InputGroup>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPending(true);
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    try {
      await fetch('/api/user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.get('name'),
          email: formData.get('email'),
        }),
      });
    } catch (error) {
      console.error('Failed to update account:', error);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-full h-[800px] p-0 overflow-hidden sm:max-w-4xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Account Settings</DialogTitle>
          <DialogDescription className="sr-only">Manage your account settings</DialogDescription>
        </DialogHeader>
        <div className="absolute top-4 right-4 z-10">
          <DialogCloseButton onClick={() => setIsOpen(false)} />
        </div>
        <div className="flex h-full max-h-[800px]">
          {/* Left Sidebar */}
          <div className="w-48 border border-panel-border bg-surface-sunken p-0 pt-5 rounded-xl ml-2 mt-2 mb-2">
            <nav className="px-3 flex flex-col gap-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-xs text-sm transition-colors cursor-pointer ${
                    activeSection === section.id
                      ? 'bg-surface-subtle text-info-solid'
                      : 'text-foreground hover:text-foreground'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Right Content */}
          <div className="flex-1 py-4 px-6 overflow-y-auto mt-5">
            {activeSection === 'general' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium mb-4">General Settings</h3>
                <div className="space-y-3">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <AccountFormWithData />
                    <Button type="submit" disabled={isPending} variant="primary">
                      {isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Changes
                    </Button>
                  </form>
                </div>
              </div>
            )}
            
            {activeSection === 'workspaces' && (
              <WorkspacesSection />
            )}

            {activeSection === 'usage' && (
              <UsageSection />
            )}

            {activeSection === 'notifications' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Notifications</h3>
                
                <div className="space-y-6">
                  {/* Mailbox Notifications */}
                  <Card className="p-1 rounded-lg border border-panel-border">
                    <CardContent className="p-1 pb-3">
                      <h3 className="text-md font-medium mb-6 ml-3 mt-2">Mailbox</h3>
                      <div className="space-y-4 ml-3">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="new-message-notification" className="text-sm font-normal m-0">
                            New messages
                          </Label>
                          <Switch className="mb-0" id="new-message-notification" defaultChecked />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="reply-notification" className="text-sm font-normal m-0">
                            Replies
                          </Label>
                          <Switch className="mb-0" id="reply-notification" defaultChecked />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="audio-notifications" className="text-sm font-normal m-0">
                            Audio notifications
                          </Label>
                          <Switch className="mb-0" id="audio-notifications" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Campaign Notifications */}
                  <Card className="p-1 rounded-lg border border-panel-border">
                    <CardContent className="p-1 pb-3">
                      <h3 className="text-md font-medium mb-6 ml-3 mt-2">Campaigns</h3>
                      <div className="space-y-4 ml-3">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="campaign-status-notification" className="text-sm font-normal m-0">
                            Campaign status changes
                          </Label>
                          <Switch className="mb-0" id="campaign-status-notification" defaultChecked />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="campaign-error-notification" className="text-sm font-normal m-0">
                            Campaign errors
                          </Label>
                          <Switch className="mb-0" id="campaign-error-notification" defaultChecked />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            
            {activeSection === 'security' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Security Settings</h3>
                
                <div className="space-y-6">
                  {/* Password Section */}
                  <Card className="bg-transparent p-0 pt-2">
                    <CardContent className="p-0">
                      <div className="space-y-6">
                        <h4 className="text-base font-medium mb-4">Change Password</h4>
                        <form className="space-y-6">
                          <InputGroup className="max-w-md">
                            <InputGroupItem
                              id="current-password"
                              name="currentPassword"
                              type="password"
                              placeholder="Enter current password"
                              inlineLabel="Current Password"
                              labelWidth="180px"
                            />
                            <InputGroupItem
                              id="new-password"
                              name="newPassword"
                              type="password"
                              placeholder="Enter new password"
                              inlineLabel="New Password"
                              labelWidth="180px"
                            />
                            <InputGroupItem
                              id="confirm-password"
                              name="confirmPassword"
                              type="password"
                              placeholder="Confirm new password"
                              inlineLabel="Confirm New Password"
                              labelWidth="180px"
                            />
                          </InputGroup>
                          <Button type="submit" variant="primary">
                            Update Password
                          </Button>
                        </form>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Delete Account Section */}
                  <Card className="bg-danger-surface-strong p-1 mt-10 rounded-lg shadow-none">
                    <CardContent className="p-1 pb-3">
                      <h4 className="text-base font-medium mb-4 text-danger-foreground ml-3 mt-2">Delete Account</h4>
                      <p className="text-content-secondary text-sm mb-4 ml-3">
                        Account deletion is non-reversible. Please proceed with caution.
                      </p>
                      <Button type="submit" variant="destructive" className="ml-3 mb-2">
                        Delete Account
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

